import { apiClient } from "./apiClient";
import { LoginCredentials } from "@/types/auth";
import logger from "@/utils/logger";
import {
  InstitutionLoginResponse,
  InstitutionUserAuth,
  UpdatePasswordData,
  CreateUserData,
  CreateMultipleUsersData,
  UpdateUserData,
  UpdateUserPasswordData,
  UpdateMultiplePasswordsData,
  UpdatePasswordAtDifferentLevelsData,
  UpdatePasswordAtDifferentLevelsResponse,
  InstitutionUser,
  UserListResponse,
  FilterUsersData,
  SearchUsersParams,
  BatchTag,
  CreateBatchTagData,
  UpdateBatchTagData,
  DeleteBatchTagData,
  BatchTagListResponse,
  Batch,
  CreateBatchData,
  UpdateBatchData,
  DeleteBatchData,
  BatchListResponse,
  BatchesByBatchYearResponse,
  BatchYear,
  CreateBatchYearData,
  UpdateBatchYearData,
  DeleteBatchYearData,
  BatchYearListResponse,
  ApiResponse,
  ToggleUserStatusData,
  ToggleUserStatusResponse,
  BulkUserUploadResponse,
  UpdateChapterData,
  DeleteChapterData,
  Chapter,
  ChapterListResponse,
  CreateChapterData,
  CreateSubjectData,
  DeleteSubjectData,
  Subject,
  SubjectListResponse,
  UpdateSubjectData,
  Faculty,
  CreateFacultyData,
  UpdateFacultyData,
  DeleteFacultyData,
  FacultyListResponse,
} from "@/types/institution-data";
import {
  PracticeReportResponse,
  ExcelPracticeReportData,
  ExcelParticipationReportData,
  ApiResponse as PracticeApiResponse
} from "@/types/practice";
import { CategoryResponse } from "@/types/assessment";

class InstitutionService {
  // Institution authentication
  async login(
    credentials: LoginCredentials,
  ): Promise<
    InstitutionLoginResponse & { require_otp?: boolean; user_id?: string }
  > {
    try {
      return await apiClient.post("/institution/login", credentials);
    } catch (error) {
      logger.error("Error logging in:", error);
      throw error;
    }
  }

  // Verify OTP for login
  async verifyLoginOTP(data: {
    user_id: string;
    otpCode: string;
    subdomain?: string;
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  }): Promise<
    InstitutionLoginResponse & { require_otp?: boolean; user_id?: string }
  > {
    try {
      return await apiClient.post("/institution/verify-login-otp", data);
    } catch (error) {
      logger.error("Error verifying login OTP:", error);
      throw error;
    }
  }

  // Update institution colors
  async updateInstitutionColors(data: {
    primary_colour: string;
    secondary_colour: string;
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/update-colors", data);
    } catch (error) {
      logger.error("Error updating institution colors:", error);
      throw error;
    }
  }

  // Get institution colors
  async getColors(): Promise<ApiResponse> {
    try {
      return await apiClient.get("/institution/colors");
    } catch (error) {
      logger.error("Error fetching institution colors:", error);
      throw error;
    }
  }

  // Get institution template status
  async getTemplateStatus(): Promise<
    ApiResponse<{ template_status: number; template_added_at: string }>
  > {
    try {
      return await apiClient.get("/institution/template/status");
    } catch (error) {
      logger.error("Error fetching institution template status:", error);
      throw error;
    }
  }

  // Get institution details (including colors)
  async getInstitutionDetails(): Promise<ApiResponse> {
    try {
      return await apiClient.get("/institution/profile");
    } catch (error) {
      logger.error("Error fetching institution details:", error);
      throw error;
    }
  }

  private logoPromise: Promise<{ success: number; logo: string | null }> | null = null;

  // Get institution logo
  async getMyLogo(): Promise<{ success: number; logo: string | null }> {
    try {
      if (typeof window !== "undefined") {
        const sub = localStorage.getItem("subdomain");
        const cachedLogo = sub ? localStorage.getItem(`logo_${sub}`) : localStorage.getItem("cached_my_logo");
        if (cachedLogo) {
          // Silently update cache
          if (!this.logoPromise) {
            this.logoPromise = apiClient.get("/institution/get-my-logo");
            this.logoPromise.then(res => {
              if (res.success && res.logo) {
                if (sub) localStorage.setItem(`logo_${sub}`, res.logo);
                localStorage.setItem("cached_my_logo", res.logo);
              }
            }).catch(() => { this.logoPromise = null; });
          }
          return { success: 1, logo: cachedLogo };
        }
      }

      if (this.logoPromise) {
        return await this.logoPromise;
      }
      this.logoPromise = apiClient.get("/institution/get-my-logo");
      const result = await this.logoPromise;
      if (result.success && result.logo && typeof window !== "undefined") {
        const sub = localStorage.getItem("subdomain");
        if (sub) localStorage.setItem(`logo_${sub}`, result.logo);
        localStorage.setItem("cached_my_logo", result.logo);
      }
      return result;
    } catch (error) {
      this.logoPromise = null; // Clear cache on error
      logger.error("Error fetching institution logo:", error);
      throw error;
    }
  }

  // Update institution template
  async updateTemplate(template: string): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/template/update", { template });
    } catch (error) {
      logger.error("Error updating institution template:", error);
      throw error;
    }
  }

  // Update institution password
  async updatePassword(data: UpdatePasswordData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/update_password", data);
    } catch (error) {
      logger.error("Error updating password:", error);
      throw error;
    }
  }

  /**
   * TELEGRAM INTEGRATION METHODS
   */

  // Generate a unique Telegram deep link for account linking
  async generateTelegramLink(): Promise<ApiResponse> {
    try {
      return await apiClient.get("/institution/telegram/generate-link");
    } catch (error) {
      logger.error("Error generating Telegram link:", error);
      throw error;
    }
  }

  // Save Telegram chat ID for the authenticated user
  async saveTelegramChatId(data: {
    telegram_chat_id: string;
    telegram_username?: string;
    telegram_first_name?: string;
    telegram_auth_data?: Record<string, any>;
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/telegram/link", data);
    } catch (error) {
      logger.error("Error saving Telegram chat ID:", error);
      throw error;
    }
  }

  // Get Telegram connection status for the authenticated user
  async getTelegramStatus(): Promise<ApiResponse> {
    try {
      return await apiClient.get("/institution/telegram/status");
    } catch (error) {
      logger.error("Error fetching Telegram status:", error);
      throw error;
    }
  }

  // Unlink Telegram account
  async unlinkTelegram(): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/telegram/unlink", {});
    } catch (error) {
      logger.error("Error unlinking Telegram:", error);
      throw error;
    }
  }

  // Logout institution
  async logout(): Promise<void> {
    try {
      // Clear token from localStorage
      if (typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("userName");
        localStorage.removeItem("userId");
      }
    } catch (error) {
      logger.error("Error logging out:", error);
      throw error;
    }
  }

  // Check if institution is authenticated
  isAuthenticated(): boolean {
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("token");
      const isAuthenticated = localStorage.getItem("isAuthenticated");
      const userRole = localStorage.getItem("userRole");

      logger.debug("Auth check details:", {
        token: token ? "exists" : "missing",
        isAuthenticated,
        userRole,
        result: !!(
          token &&
          isAuthenticated === "true" &&
          userRole === "institution"
        ),
      });

      return !!(
        token &&
        isAuthenticated === "true" &&
        userRole === "institution"
      );
    }
    return false;
  }

  // Get stored authentication token
  getToken(): string | null {
    if (typeof window !== "undefined") {
      return localStorage.getItem("token");
    }
    return null;
  }

  // Store authentication data
  setAuthData(data: { token: string; user: InstitutionUserAuth }): void {
    if (typeof window !== "undefined") {
      logger.debug("Storing auth data:", {
        token: data.token ? "exists" : "missing",
        userRole: data.user.user_role,
        email: data.user.email,
        uuid: data.user.uuid,
      });

      localStorage.setItem("token", data.token);
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("userRole", "institution");
      localStorage.setItem("userEmail", data.user.email);
      localStorage.setItem("userId", data.user.uuid);

      logger.debug("Auth data stored successfully");
    }
  }

  /**
   * BATCH YEAR MANAGEMENT METHODS
   */

  // Create a new batch year

  async createBatchYear(
    data: CreateBatchYearData,
  ): Promise<ApiResponse<BatchYear>> {
    try {
      return await apiClient.post("/institution/batch_year/create", data);
    } catch (error) {
      logger.error("Error creating batch year:", error);
      throw error;
    }
  }

  //Delete a batch year

  async deleteBatchYear(data: DeleteBatchYearData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/batch_year/delete", data);
    } catch (error) {
      logger.error("Error deleting batch year:", error);
      throw error;
    }
  }

  //Update an existing batch year

  async updateBatchYear(
    data: UpdateBatchYearData,
  ): Promise<ApiResponse<BatchYear>> {
    try {
      return await apiClient.post("/institution/batch_year/update", data);
    } catch (error) {
      logger.error("Error updating batch year:", error);
      throw error;
    }
  }

  //Get all batch years

  async getBatchYearList(): Promise<BatchYearListResponse> {
    try {
      logger.debug(
        "Calling batch years list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/batch_year/list");
      logger.debug("Batch years API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching batch years:", error);
      throw error;
    }
  }

  //Get a specific batch year by ID

  async getBatchYearById(batchYearId: string): Promise<ApiResponse<BatchYear>> {
    try {
      return await apiClient.get(`/institution/batch_year/${batchYearId}`);
    } catch (error) {
      logger.error("Error fetching batch year:", error);
      throw error;
    }
  }

  private _batch_year_names_promise: Promise<ApiResponse<{ uuid: string; name: string }[]>> | null = null;
  private _batches_by_year_promise_map = new Map<string, Promise<ApiResponse<{ uuid: string; name: string }[]>>>();

  // Get batch year names (optimized for dropdowns with request deduplication)
  async getBatchYearNames(): Promise<
    ApiResponse<{ uuid: string; name: string }[]>
  > {
    if (this._batch_year_names_promise) {
      return this._batch_year_names_promise;
    }
    try {
      this._batch_year_names_promise = apiClient.get(`/institution/batch_year/names`);
      const response = await this._batch_year_names_promise;
      setTimeout(() => {
        this._batch_year_names_promise = null;
      }, 15000);
      return response;
    } catch (error) {
      this._batch_year_names_promise = null;
      logger.error("Error fetching batch year names:", error);
      throw error;
    }
  }

  // Get batch names by batch year (optimized for dropdowns with request deduplication)
  async getBatchesNamesByBatchYear(
    batchYearId: string,
  ): Promise<ApiResponse<{ uuid: string; name: string }[]>> {
    if (!batchYearId) return { success: false, data: [] } as any;
    if (this._batches_by_year_promise_map.has(batchYearId)) {
      return this._batches_by_year_promise_map.get(batchYearId)!;
    }
    try {
      const promise = apiClient.get(`/institution/batches/names/${batchYearId}`);
      this._batches_by_year_promise_map.set(batchYearId, promise);
      const response = await promise;
      setTimeout(() => {
        this._batches_by_year_promise_map.delete(batchYearId);
      }, 15000);
      return response;
    } catch (error) {
      this._batches_by_year_promise_map.delete(batchYearId);
      logger.error("Error fetching batch names by batch year:", error);
      throw error;
    }
  }

  /**
   * BATCH TAG MANAGEMENT METHODS
   */

  //Create a new batch tag
  async createBatchTag(
    data: CreateBatchTagData,
  ): Promise<ApiResponse<BatchTag>> {
    try {
      return await apiClient.post("/institution/batch_tag/create", data);
    } catch (error) {
      logger.error("Error creating batch tag:", error);
      throw error;
    }
  }

  // Update an existing batch tag
  async updateBatchTag(
    data: UpdateBatchTagData,
  ): Promise<ApiResponse<BatchTag>> {
    try {
      return await apiClient.post("/institution/batch_tag/update", data);
    } catch (error) {
      logger.error("Error updating batch tag:", error);
      throw error;
    }
  }

  // Get all batch tags
  async getBatchTagsList(): Promise<BatchTagListResponse> {
    try {
      logger.debug(
        "Calling batch tags list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/batch_tags/list");
      logger.debug("Batch tags API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching batch tags:", error);
      throw error;
    }
  }

  // Get batch tags by batch year ID

  async getBatchTagsByBatchYearId(
    batchYearId: string,
  ): Promise<BatchTagListResponse> {
    try {
      return await apiClient.get(`/institution/batch_tags/${batchYearId}`);
    } catch (error) {
      logger.error("Error fetching batch tags by batch year:", error);
      throw error;
    }
  }

  // Get a specific batch tag by ID
  async getBatchTagById(batchTagId: number): Promise<ApiResponse<BatchTag>> {
    try {
      return await apiClient.get(`/institution/batch_tag/${batchTagId}`);
    } catch (error) {
      logger.error("Error fetching batch tag:", error);
      throw error;
    }
  }

  // Delete a batch tag

  async deleteBatchTag(data: DeleteBatchTagData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/batch_tag/delete", data);
    } catch (error) {
      logger.error("Error deleting batch tag:", error);
      throw error;
    }
  }

  /**
   * BATCH MANAGEMENT METHODS
   */

  // Create a new batch

  async createBatch(data: CreateBatchData): Promise<ApiResponse<Batch>> {
    try {
      return await apiClient.post("/institution/batch/create", data);
    } catch (error) {
      logger.error("Error creating batch:", error);
      throw error;
    }
  }

  // Delete a batch
  async deleteBatch(data: DeleteBatchData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/batch/delete", data);
    } catch (error) {
      logger.error("Error deleting batch:", error);
      throw error;
    }
  }

  // Update an existing batch

  async updateBatch(data: UpdateBatchData): Promise<ApiResponse<Batch>> {
    try {
      return await apiClient.post("/institution/batch/update", data);
    } catch (error) {
      logger.error("Error updating batch:", error);
      throw error;
    }
  }

  // Get all batches
  async getBatchesList(): Promise<BatchListResponse> {
    try {
      logger.debug(
        "Calling batches list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/batches/list");
      logger.debug("Batches API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching batches:", error);
      throw error;
    }
  }

  // Get batches by batch tag ID
  // async getBatchesByBatchTag(batchTagId: string): Promise<BatchListResponse> {
  //   try {
  //     return await apiClient.get(`/institution/batches/${batchTagId}`);
  //   } catch (error) {
  //     console.error("Error fetching batches by batch tag:", error);
  //     throw error;
  //   }
  // }

  // Get a specific batch by ID
  async getBatchById(batchId: string): Promise<ApiResponse<Batch>> {
    try {
      return await apiClient.get(`/institution/batch/${batchId}`);
    } catch (error) {
      logger.error("Error fetching batch:", error);
      throw error;
    }
  }

  //  Get batches by batch year ID
  async getBatchesByBatchYear(
    batchYearId: string,
  ): Promise<BatchesByBatchYearResponse> {
    try {
      return await apiClient.get(`/institution/batches/${batchYearId}`);
    } catch (error) {
      logger.error("Error fetching batches by batch year:", error);
      throw error;
    }
  }

  // Get Evaluation Data
  async getEvaluationData(params?: {
    passout_year_id?: string;
    batch_id?: string;
  }): Promise<{
    success: number;
    speaking_limit: number;
    writing_limit: number;
    total_speaking_usage: number;
    total_speaking_billing: number;
    total_writing_usage: number;
    total_writing_billing: number;
  }> {
    try {
      return await apiClient.post('/institution/evaluation', params || {});
    } catch (error) {
      logger.error('Error fetching evaluation data:', error);
      throw error;
    }
  }

  // Verify password for institution
  async verifyPassword(password: string): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/verify-password", { password });
    } catch (error) {
      logger.error("Error verifying password:", error);
      throw error;
    }
  }

  /**
   * USER MANAGEMENT METHODS
   */

  // Get all users
  async getUsers(): Promise<UserListResponse> {
    try {
      logger.debug(
        "Calling users API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/users");
      logger.debug("Users API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching users:", error);
      throw error;
    }
  }

  // Get filtered users by batch_year_id and/or batch_id
  async getFilteredUsers(filters: FilterUsersData): Promise<UserListResponse> {
    try {
      return await apiClient.post("/institution/filter_users", filters);
    } catch (error) {
      logger.error("Error fetching filtered users:", error);
      throw error;
    }
  }

  // Get users list by batch_year and/or batch
  async getUsersList(data: {
    batch_year?: string;
    batch?: string;
  }): Promise<{ success: number; users: InstitutionUser[] }> {
    try {
      return await apiClient.post("/institution/usersList", data);
    } catch (error) {
      logger.error("Error fetching users list:", error);
      throw error;
    }
  }

  // Get filtered class users report (12 fields)
  async getClassUsersReport(data: {
    department_id?: string;
    batch_year?: string | string[];
    batch?: string | string[];
  }): Promise<{ success: number; users: any[] }> {
    try {
      return await apiClient.post("/institution/users/class-report", data);
    } catch (error) {
      logger.error("Error fetching class users report:", error);
      throw error;
    }
  }

  // Get single user full report data
  async getUserFullReport(userId: string): Promise<{ success: number; data: any }> {
    try {
      const res: any = await apiClient.get(`/institution/users/full-report/${userId}`);
      if (res && (res.success === 1 || res.success === true)) {
        return res;
      }
      return res || { success: 0, data: { trainings: [] } };
    } catch (error) {
      logger.error("Error fetching user full report:", error);
      return { success: 0, data: { trainings: [] } };
    }
  }

  // Search users by query (email, name, regd, mobile)
  async getSearchUsers(params: SearchUsersParams): Promise<UserListResponse> {
    try {
      const queryString = new URLSearchParams({
        query: Array.isArray(params.query)
          ? params.query.join(",")
          : params.query,
      }).toString();
      return await apiClient.get(`/institution/search_users?${queryString}`);
    } catch (error) {
      logger.error("Error searching users:", error);
      throw error;
    }
  }

  // Create a new user
  async createUser(
    data: CreateUserData,
  ): Promise<ApiResponse<InstitutionUser>> {
    try {
      return await apiClient.post("/institution/user/create", data);
    } catch (error) {
      logger.error("Error creating user:", error);
      throw error;
    }
  }

  // Create multiple users from file file
  async createMultipleUsers(
    data: CreateMultipleUsersData,
  ): Promise<BulkUserUploadResponse> {
    try {
      logger.debug("Creating multiple users with data:", {
        fileName: data.excel.name,
        fileType: data.excel.type,
        fileSize: data.excel.size,
        batch_id: data.batch_id,
      });

      const formData = new FormData();

      // Append the original file directly - don't recreate it to avoid data corruption
      formData.append("file", data.excel, data.excel.name);
      formData.append("batch_id", data.batch_id);

      if (data.batch_year_id) {
        formData.append("batch_year_id", data.batch_year_id);
      }

      if (data.access_period) {
        formData.append("access_period", data.access_period);
      }

      logger.debug(
        "FormData prepared with original file, sending to backend...",
      );
      logger.debug(
        "FormData prepared with original file, sending to backend...",
      );

      return await apiClient.post("/institution/multi_users/create", formData);
    } catch (error) {
      logger.error("Error creating multiple users:", error);
      throw error;
    }
  }

  // Update an existing user
  async updateUser(
    data: UpdateUserData,
  ): Promise<ApiResponse<InstitutionUser>> {
    try {
      return await apiClient.post("/institution/user/update", data);
    } catch (error) {
      logger.error("Error updating user:", error);
      throw error;
    }
  }

  // Delete a user
  async deleteUser(userId: string): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/user/delete", {
        id: userId,
      });
    } catch (error) {
      logger.error("Error deleting user:", error);
      throw error;
    }
  }

  // Move users to different batch
  async moveUsers(data: {
    users: string[];
    batch_id: string;
    batch_year_id: string;
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/users/move", data);
    } catch (error) {
      logger.error("Error moving users:", error);
      throw error;
    }
  }

  // Get a specific user by ID
  async getUserById(userId: string): Promise<ApiResponse<InstitutionUser>> {
    try {
      return await apiClient.get(`/institution/user/${userId}`);
    } catch (error) {
      logger.error("Error fetching user:", error);
      throw error;
    }
  }

  // Get user profile details for the view card
  async getUserDetails(userId: string): Promise<ApiResponse<InstitutionUser>> {
    try {
      return await apiClient.get(`/institution/user/${userId}/details`);
    } catch (error) {
      logger.error("Error fetching user details:", error);
      throw error;
    }
  }

  // Get assessment activity for a user (Assessments tab)
  async getUserAssessmentActivity(userId: string): Promise<any> {
    try {
      const res: any = await apiClient.get(`/institution/user/${userId}/assessment-activity`);
      if (res && (res.success === 1 || res.success === true) && (res.score_distribution || res.data?.score_distribution)) {
        return res;
      }
      return await apiClient.get(`/vendor/user/${userId}/assessment-activity`);
    } catch (error) {
      logger.error("Error fetching user assessment activity:", error);
      try {
        return await apiClient.get(`/vendor/user/${userId}/assessment-activity`);
      } catch (err) {
        return { success: 0, student_exams: [] };
      }
    }
  }

  // Get users by batch ID
  async getUsersByBatch(batchId: string): Promise<UserListResponse> {
    try {
      return await apiClient.get(`/institution/users/batch/${batchId}`);
    } catch (error) {
      logger.error("Error fetching users by batch:", error);
      throw error;
    }
  }

  // Get users by batch year ID (returns users grouped by batches)
  async getUsersByBatchYear(
    batchYearId: string,
  ): Promise<BatchesByBatchYearResponse> {
    try {
      return await apiClient.get(
        `/institution/users/batch_year/${batchYearId}`,
      );
    } catch (error) {
      logger.error("Error fetching users by batch year:", error);
      throw error;
    }
  }

  // Get users by batch tag ID (returns users grouped by batches)
  async getUsersByBatchTag(
    batchTagId: string,
  ): Promise<BatchesByBatchYearResponse> {
    try {
      return await apiClient.get(`/institution/users/batch_tag/${batchTagId}`);
    } catch (error) {
      logger.error("Error fetching users by batch tag:", error);
      throw error;
    }
  }

  // Update user password
  async updateUserPassword(data: UpdateUserPasswordData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/user/update_password", data);
    } catch (error) {
      logger.error("Error updating user password:", error);
      throw error;
    }
  }

  // Update multiple users passwords with same password
  async updateMultipleUsersPassword(
    data: UpdateMultiplePasswordsData,
  ): Promise<ApiResponse> {
    try {
      return await apiClient.post(
        "/institution/user/update_multiple_passwords",
        data,
      );
    } catch (error) {
      logger.error("Error updating multiple users passwords:", error);
      throw error;
    }
  }

  // Update passwords at different levels (batch year, batch, or all users)
  async updatePasswordsAtDifferentLevels(
    data: UpdatePasswordAtDifferentLevelsData,
  ): Promise<UpdatePasswordAtDifferentLevelsResponse> {
    try {
      return await apiClient.post(
        "/institution/users/update_password_at_levels",
        data,
      );
    } catch (error) {
      logger.error("Error updating passwords at different levels:", error);
      throw error;
    }
  }

  // Toggle user status (active/inactive)
  async toggleUserStatus(
    data: ToggleUserStatusData,
  ): Promise<ToggleUserStatusResponse> {
    try {
      return await apiClient.post("/institution/user/toggle_status", data);
    } catch (error) {
      logger.error("Error toggling user status:", error);
      throw error;
    }
  }

  /**
   * SUBJECT MANAGEMENT METHODS
   */

  // Create a new subject
  async createSubject(data: CreateSubjectData): Promise<ApiResponse<Subject>> {
    try {
      return await apiClient.post("/institution/subject/create", data);
    } catch (error) {
      logger.error("Error creating subject:", error);
      throw error;
    }
  }

  // Delete a subject
  async deleteSubject(data: DeleteSubjectData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/subject/delete", data);
    } catch (error) {
      logger.error("Error deleting subject:", error);
      throw error;
    }
  }

  // Update an existing subject
  async updateSubject(data: UpdateSubjectData): Promise<ApiResponse<Subject>> {
    try {
      return await apiClient.post("/institution/subject/update", data);
    } catch (error) {
      logger.error("Error updating subject:", error);
      throw error;
    }
  }

  // Get all subjects
  async getSubjectList(): Promise<SubjectListResponse> {
    try {
      logger.debug(
        "Calling subjects list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/subjects/list");
      logger.debug("Subjects API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching subjects:", error);
      throw error;
    }
  }

  // Get a specific subject by ID
  async getSubjectById(subjectId: string): Promise<ApiResponse<Subject>> {
    try {
      return await apiClient.get(`/institution/subject/${subjectId}`);
    } catch (error) {
      logger.error("Error fetching subject:", error);
      throw error;
    }
  }

  // Get all subject tags
  async getSubjectTags(): Promise<any> {
    try {
      return await apiClient.get("/institution/getSubjectTags");
    } catch (error) {
      logger.error("Error fetching subject tags:", error);
      throw error;
    }
  }

  /**
   * CHAPTER MANAGEMENT METHODS
   */

  // Create a new chapter
  async createChapter(data: CreateChapterData): Promise<ApiResponse<Chapter>> {
    try {
      return await apiClient.post("/institution/chapter/create", data);
    } catch (error) {
      logger.error("Error creating chapter:", error);
      throw error;
    }
  }

  // Delete a chapter
  async deleteChapter(data: DeleteChapterData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/chapter/delete", data);
    } catch (error) {
      logger.error("Error deleting chapter:", error);
      throw error;
    }
  }

  // Update an existing chapter
  async updateChapter(data: UpdateChapterData): Promise<ApiResponse<Chapter>> {
    try {
      return await apiClient.post("/institution/chapter/update", data);
    } catch (error) {
      logger.error("Error updating chapter:", error);
      throw error;
    }
  }

  // Get all chapters
  async getChaptersList(): Promise<ChapterListResponse> {
    try {
      logger.debug(
        "Calling chapters list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/chapters/list");
      logger.debug("Chapters API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching chapters:", error);
      throw error;
    }
  }

  // Get a specific chapter by ID
  async getChapterById(chapterId: string): Promise<ApiResponse<Chapter>> {
    try {
      return await apiClient.get(`/institution/chapter/${chapterId}`);
    } catch (error) {
      logger.error("Error fetching chapter:", error);
      throw error;
    }
  }

  // Get chapters by subject ID
  async getChaptersBySubject(subjectId: string): Promise<ChapterListResponse> {
    try {
      return await apiClient.get(`/institution/chapters/${subjectId}`);
    } catch (error) {
      logger.error("Error fetching chapters by subject:", error);
      throw error;
    }
  }

  /**
   * CATEGORY ACCESS MANAGEMENT METHODS (INSTITUTION-SPECIFIC)
   */

  // Create category access for institution
  // Note: institution_id is not sent - backend uses req.user.id from JWT
  async createCategoryAccess(data: {
    batch_year_id: string;
    batch_id: string[];
    category_id: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Creating category access", data);
      return await apiClient.post("/institution/category/access/create", data);
    } catch (error) {
      logger.error("Error creating institution category access:", error);
      throw error;
    }
  }

  // Get category access list
  async getCategoryAccessList(categoryId: string): Promise<any> {
    try {
      logger.debug(
        "Institution Service: Fetching category access list for:",
        categoryId,
      );
      return await apiClient.get(
        `/institution/category/access/list/${categoryId}`,
      );
    } catch (error) {
      logger.error("Error fetching institution category access list:", error);
      throw error;
    }
  }

  // Get single assessment category details (institution-scoped endpoint)
  async getCategoryDetailsById(id: string): Promise<CategoryResponse> {
    try {
      logger.debug(
        "Institution Service: Fetching category details for:",
        id,
      );
      return await apiClient.get(
        `/institution/assessment/category/${id}/details`,
      );
    } catch (error) {
      logger.error("Error fetching institution category details:", error);
      throw error;
    }
  }

  // Update category access
  async updateCategoryAccess(data: {
    id: string;
    batch_year_id: string;
    batch_id: string[];
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Updating category access", data);
      return await apiClient.post("/institution/category/access/update", data);
    } catch (error) {
      logger.error("Error updating institution category access:", error);
      throw error;
    }
  }

  // Delete category access
  async deleteCategoryAccess(data: { id: string }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Deleting category access", data);
      return await apiClient.post("/institution/category/access/delete", data);
    } catch (error) {
      logger.error("Error deleting institution category access:", error);
      throw error;
    }
  }

  /**
   * EXAM ACCESS MANAGEMENT METHODS (INSTITUTION-SPECIFIC)
   */

  // Get exam access list for a specific exam
  // Returns list of batches with their access status for the exam
  async getExamAccessList(categoryId: string, examId: string): Promise<any> {
    try {
      logger.debug(
        "Institution Service: Fetching exam access list for:",
        categoryId,
        examId,
      );
      return await apiClient.get(`/institution/exam/access/list/${categoryId}`);
    } catch (error) {
      logger.error("Error fetching institution exam access list:", error);
      throw error;
    }
  }

  // Get category access with exam details (mirrors vendor getCategoryAccessWithExam)
  // Used by assessments access page to get list of batches with access status
  async getCategoryAccessWithExam(
    categoryId: string,
    examId: string,
  ): Promise<any> {
    try {
      logger.debug(
        "Institution Service: Fetching category access with exam for:",
        categoryId,
        examId,
      );
      return await apiClient.get(
        `/institution/getCategoryAccess/${categoryId}/${examId}`,
      );
    } catch (error) {
      logger.error("Error fetching category access with exam:", error);
      throw error;
    }
  }

  // Insert or update exam access for a specific batch
  async insertExamAccess(data: {
    id?: string;
    access_id?: string | null;
    exam_id: string;
    batch_year: string;
    batch: string;
    access?: boolean | number;
    from?: string;
    to?: string;
    fromTime?: string;
    toTime?: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Inserting exam access", data);
      return await apiClient.post("/institution/exam/access/insert", data);
    } catch (error) {
      logger.error("Error inserting exam access:", error);
      throw error;
    }
  }

  // Update access time selection for a specific batch
  async updateAccessTimeSelection(data: {
    id: number;
    from: string;
    to: string;
    fromTime: string;
    toTime: string;
    auto_submit?: 0 | 1;
    auto_submit_time?: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Updating access time selection", data);
      return await apiClient.post("/institution/exam/access/update-time", data);
    } catch (error) {
      logger.error("Error updating access time selection:", error);
      throw error;
    }
  }

  // Update global access time for all batches with access to an exam
  async updateGlobalAccessTime(data: {
    exam_id: string;
    from: string;
    to: string;
    fromTime: string;
    toTime: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Updating global access time", data);
      return await apiClient.post(
        "/institution/exam/access/update-global-time",
        data,
      );
    } catch (error) {
      logger.error("Error updating global access time:", error);
      throw error;
    }
  }

  // Grant exam access to all specified batches
  async grantExamAccessToAll(data: {
    exam_id: string;
    batches: Array<{
      batch: string;
      batch_year: string;
    }>;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Granting exam access to all", data);
      return await apiClient.post("/institution/exam/access/grant-all", data);
    } catch (error) {
      logger.error("Error granting exam access to all:", error);
      throw error;
    }
  }

  // Grant exam access to selected batches
  async grantExamAccessToBulkBatches(data: {
    category_id: string;
    exam_id: string;
    batches: string[];
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Granting exam access to bulk batches", data);
      return await apiClient.post("/institution/exam/access/grant-bulk-batches", data);
    } catch (error) {
      logger.error("Error granting exam access to bulk batches:", error);
      throw error;
    }
  }

  // Grant exam access to selected users
  async grantExamAccessToBulkUsers(data: {
    category_id: string;
    exam_id: string;
    users: string[];
    type?: "email" | "regd";
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Granting exam access to bulk users", data);
      return await apiClient.post("/institution/exam/access/grant-bulk-users", data);
    } catch (error) {
      logger.error("Error granting exam access to bulk users:", error);
      throw error;
    }
  }

  // Update user level access setting for an exam
  async updateExamUserLevelAccess(data: {
    checked: number;
    access_id: number;
    batch: string;
    exam_id: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Updating exam user level access", data);
      return await apiClient.post(
        "/institution/exam/access/user-level/update",
        data,
      );
    } catch (error) {
      logger.error("Error updating exam user level access:", error);
      throw error;
    }
  }

  // Get list of users with their exam access status for a batch
  async getExamAccessUsersList(data: {
    batch: string;
    exam_id: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Getting exam access users list", data);
      return await apiClient.post("/institution/exam/access/users/list", data);
    } catch (error) {
      logger.error("Error getting exam access users list:", error);
      throw error;
    }
  }

  // Change exam access for a specific user
  async changeUserAccessForExam(data: {
    user_id: string;
    batch_id: string;
    exam_id: string;
    checked: number;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Changing user access for exam", data);
      return await apiClient.post("/institution/exam/access/user/change", data);
    } catch (error) {
      logger.error("Error changing user access for exam:", error);
      throw error;
    }
  }

  // Grant exam access by email or registration number
  async grantEmailAccess(data: {
    emails: string;
    isEmail: boolean | number;
    batch: string;
    exam_id: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Granting email access", data);
      return await apiClient.post("/institution/exam/access/grant-email", data);
    } catch (error) {
      logger.error("Error granting email access:", error);
      throw error;
    }
  }

  /**
   * Get all learning path access for the institution
   */
  async getLearningPathAccessList(): Promise<{
    success: number;
    message: string;
    total: number;
    data: Array<{
      id: number;
      uuid: string;
      title: string;
    }>;
  }> {
    try {
      const response = await apiClient.get("/institution/course/access");
      return response;
    } catch (error) {
      logger.error("Error getting learning path access list:", error);
      throw error;
    }
  }

  /**
   * Get learning path access details for a specific learning path
   */
  async getLearningPathAccess(learningPathId: string): Promise<{
    success: number;
    message: string;
    total: number;
    data: Array<{
      id: number;
      learning_path_id: string;
      institute_id: string;
      institute: string;
      batch_year: string;
      batch_year_name: string;
      batch: string;
      batch_name: string;
      access: number;
      user_id: string;
      user_role: string;
      created_at: string;
      updated_at: string;
    }>;
  }> {
    try {
      const response = await apiClient.get(
        `/institution/course/access/${learningPathId}`,
      );
      return response;
    } catch (error) {
      logger.error("Error getting learning path access:", error);
      throw error;
    }
  }

  /**
   * Add learning path access for institution/batch
   */
  async addLearningPathAccess(data: {
    learning_path_id: string;
    institution: string;
    batch_year: string;
    batches: string[];
  }): Promise<{
    success: number;
    message: string;
    total_batches?: number;
  }> {
    try {
      const response = await apiClient.post(
        "/institution/course/access/add",
        data,
      );
      return response;
    } catch (error) {
      logger.error("Error adding learning path access:", error);
      throw error;
    }
  }

  /**
   * Toggle learning path access (enable/disable)
   */
  async toggleLearningPathAccess(accessId: number): Promise<{
    success: number;
    message: string;
    data?: {
      id: number;
      previous_access: number;
      current_access: number;
    };
  }> {
    try {
      const response = await apiClient.post(
        `/institution/course/access/${accessId}/toggle`,
      );
      return response;
    } catch (error) {
      logger.error("Error toggling learning path access:", error);
      throw error;
    }
  }

  /**
   * Get all learning path access for the institution
   */
  async getAllLearningPathAccess(): Promise<{
    success: number;
    message: string;
    total: number;
    data: Array<{
      id: number;
      learning_path_id: string;
      learning_path_name: string;
      institute_id: string;
      institute: string;
      batch_year: string;
      batch_year_name: string;
      batch: string;
      batch_name: string;
      access: number;
      user_id: string;
      user_role: string;
      created_at: string;
      updated_at: string;
    }>;
  }> {
    try {
      const response = await apiClient.get(
        "/institution/learning-path-access/list",
      );
      return response;
    } catch (error) {
      logger.error("Error getting all learning path access:", error);
      throw error;
    }
  }

  /**
   * FACULTY MANAGEMENT METHODS
   */

  // Create a new faculty member
  async createFaculty(data: CreateFacultyData): Promise<ApiResponse<Faculty>> {
    try {
      return await apiClient.post("/institution/faculty/create", data);
    } catch (error) {
      logger.error("Error creating faculty:", error);
      throw error;
    }
  }

  // Get all faculty members
  async getFacultyList(): Promise<FacultyListResponse> {
    try {
      logger.debug(
        "Calling faculty list API with token:",
        this.getToken() ? "exists" : "missing",
      );
      const response = await apiClient.get("/institution/faculty/list");
      logger.debug("Faculty API response:", response);
      return response;
    } catch (error) {
      logger.error("Error fetching faculty list:", error);
      throw error;
    }
  }

  // Get a specific faculty by ID
  async getFacultyById(facultyId: string): Promise<ApiResponse<Faculty>> {
    try {
      return await apiClient.get(`/institution/faculty/${facultyId}`);
    } catch (error) {
      logger.error("Error fetching faculty:", error);
      throw error;
    }
  }

  // Update an existing faculty member
  async updateFaculty(data: UpdateFacultyData): Promise<ApiResponse<Faculty>> {
    try {
      return await apiClient.post("/institution/faculty/update", data);
    } catch (error) {
      logger.error("Error updating faculty:", error);
      throw error;
    }
  }

  // Delete a faculty member
  async deleteFaculty(data: DeleteFacultyData): Promise<ApiResponse> {
    try {
      return await apiClient.post("/institution/faculty/delete", data);
    } catch (error) {
      logger.error("Error deleting faculty:", error);
      throw error;
    }
  }

  /**
   * FACULTY MAPPING METHODS
   */

  // Create or update faculty mappings (batch access)
  async createFacultyMappings(data: {
    faculty_id: string;
    batch_year_id: string;
    batches: Array<{
      batch_id: string;
      user_ids?: string[];
    }>;
  }): Promise<{
    success: number;
    message: string;
    data?: {
      faculty_id: string;
      batch_year_id: string;
      total_mappings: number;
      mappings: Array<{
        id: string;
        faculty_id: string;
        batch_year_id: string;
        batch_id: string;
        user_level_mapping: number;
        user_ids: string | null;
      }>;
      skipped_items?: Array<{
        batch_id?: string;
        user_id?: string;
        reason?: string;
        [key: string]: any;
      }>;
    };
  }> {
    try {
      logger.debug("Institution Service: Creating faculty mappings", data);
      return await apiClient.post("/institution/faculty/mappings/create", data);
    } catch (error) {
      logger.error("Error creating faculty mappings:", error);
      throw error;
    }
  }

  // Update faculty mappings (batch access)
  async updateFacultyMappings(data: {
    faculty_id: string;
    batch_year_id: string;
    batches: Array<{
      batch_id: string;
      user_ids?: string[];
    }>;
  }): Promise<{
    success: number;
    message: string;
    data?: {
      faculty_id: string;
      batch_year_id: string;
      total_mappings: number;
      mappings: Array<{
        id: string;
        faculty_id: string;
        batch_year_id: string;
        batch_id: string;
        user_level_mapping: number;
        user_ids: string | null;
      }>;
      skipped_items?: Array<{
        batch_id?: string;
        user_id?: string;
        reason?: string;
        [key: string]: any;
      }>;
    };
  }> {
    try {
      logger.debug("Institution Service: Updating faculty mappings", data);
      return await apiClient.post("/institution/faculty/mappings/update", data);
    } catch (error) {
      logger.error("Error updating faculty mappings:", error);
      throw error;
    }
  }

  // Bulk verify faculty users by emails / regd numbers
  async bulkVerifyFacultyUsers(data: {
    faculty_id: string;
    user_identifiers: string[];
  }): Promise<{
    success: number;
    message: string;
    data?: {
      ready_users: Array<{
        uuid: string;
        name: string;
        email: string;
        regd?: string;
        batch_id: string;
        batch_year_id: string;
        batch_name?: string;
      }>;
      skipped_items: Array<{
        user_name: string;
        regd?: string;
        faculty_name: string;
        reason?: string;
      }>;
      not_found_identifiers: string[];
      total_verified: number;
    };
  }> {
    try {
      logger.debug("Institution Service: Bulk verifying faculty users", data);
      return await apiClient.post("/institution/faculty/mappings/bulk-verify", data);
    } catch (error) {
      logger.error("Error in bulk verify faculty users:", error);
      throw error;
    }
  }

  // Bulk assign verified faculty user access
  async bulkAssignFacultyUserAccess(data: {
    faculty_id: string;
    user_ids: string[];
  }): Promise<{
    success: number;
    message: string;
    data?: {
      total_mappings: number;
    };
  }> {
    try {
      logger.debug("Institution Service: Bulk assigning faculty user access", data);
      return await apiClient.post("/institution/faculty/mappings/bulk-user-assign", data);
    } catch (error) {
      logger.error("Error in bulk assign faculty user access:", error);
      throw error;
    }
  }

  // Get all faculty mappings (grouped by batch year)
  async getFacultyMappings(facultyId: string): Promise<{
    success: number;
    message: string;
    data: Array<{
      batch_year_id: string;
      batch_year_name: string;
      batches: Array<{
        id: string;
        batch_id: string;
        batch_name: string;
        user_level_mapping: number;
        user_ids: string[];
      }>;
    }>;
  }> {
    try {
      logger.debug(
        "Institution Service: Fetching faculty mappings for:",
        facultyId,
      );
      return await apiClient.get(`/institution/faculty/mappings/${facultyId}`);
    } catch (error) {
      logger.error("Error fetching faculty mappings:", error);
      throw error;
    }
  }

  // Get faculty mappings for a specific batch year
  async getFacultyMappingsByBatchYear(
    facultyId: string,
    batchYearId: string,
  ): Promise<{
    success: number;
    message: string;
    data: {
      batch_year_id: string;
      batch_year_name: string;
      batches: Array<{
        id: string;
        batch_id: string;
        batch_name: string;
        user_level_mapping: number;
        user_ids: string[];
      }>;
    };
  }> {
    try {
      logger.debug(
        "Institution Service: Fetching faculty mappings for batch year:",
        facultyId,
        batchYearId,
      );
      return await apiClient.get(
        `/institution/faculty/mappings/${facultyId}/${batchYearId}`,
      );
    } catch (error) {
      logger.error("Error fetching faculty mappings by batch year:", error);
      throw error;
    }
  }

  // Delete a specific faculty mapping
  async deleteFacultyMapping(data: {
    mapping_id: string;
  }): Promise<ApiResponse> {
    try {
      logger.debug("Institution Service: Deleting faculty mapping", data);
      return await apiClient.post("/institution/faculty/mappings/delete", data);
    } catch (error) {
      logger.error("Error deleting faculty mapping:", error);
      throw error;
    }
  }


  // ==================== TRAINING MANAGEMENT METHODS ====================

  // Create a new training
  async createTraining(data: {
    title: string;
    institution_id?: string;
    description?: string;
    category: string;
    training_type?: string;
    image?: string;
    start_date: string;
    end_date: string;
    daily_start_time: string;
    daily_end_time: string;
    max_participants: number;
    primary_trainer_id?: string | null;
    requirements?: string;
    skills_learned?: string;
    status?: "draft" | "active" | "scheduled" | "completed" | "cancelled";
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post("/trainings/create", data);
    } catch (error) {
      logger.error("Error creating training:", error);
      throw error;
    }
  }

  /**
   * Get institution-level leaderboard for the authenticated institution
   */
  async getInstitutionLeaderboard(filters?: {
    department?: string;
    passout_year?: string;
  }): Promise<{
    success: number;
    message: string;
    data?: {
      institution_name: string;
      total_students: number;
      available_passout_years?: string[];
      available_departments?: string[];
      passout_year_departments?: Record<string, string[]>;
      leaderboard: Array<{
        rank: number;
        student_name: string;
        roll_no?: string;
        assessment_points: number;
        performance_tier?: string;
        department_name?: string;
        passout_year?: string;
      }>;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.department)
        queryParams.append("department", filters.department);
      if (filters?.passout_year)
        queryParams.append("passout_year", filters.passout_year);
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await apiClient.get(
        `/institution/leaderboard/institution${qs}`,
      );
      return response;
    } catch (error) {
      logger.error("Get institution leaderboard error:", error);
      throw error;
    }
  }

  /**
   * Get practice test report summary and student attempts
   */
  async getPracticeReport(id: string): Promise<PracticeReportResponse> {
    try {
      return await apiClient.post(`/institution/practice-report/${id}`, {});
    } catch (error) {
      logger.error("Error fetching practice report:", error);
      throw error;
    }
  }

  /**
   * Get user-specific practice report attempts (Institution)
   */
  async getUserPracticeReport(examId: string, userId: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/practice/report/${examId}/${userId}`);
    } catch (error) {
      logger.error("Error fetching institution user report:", error);
      throw error;
    }
  }

  /**
   * Get solutions for a practice attempt (Institution)
   */
  async getInstitutionPracticeSolutions(attemptId: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/practice/solutions/${attemptId}`);
    } catch (error) {
      logger.error("Error fetching institution practice solutions:", error);
      throw error;
    }
  }

  /**
   * Get practice test report excel data
   */
  async getExcelPracticeReport(data: ExcelPracticeReportData): Promise<any> {
    try {
      return await apiClient.post("/institution/reports/practice-excel", data);
    } catch (error) {
      logger.error("Error fetching excel practice report:", error);
      throw error;
    }
  }

  /**
   * Get participation report excel data for assigned exams
   */
  async getAssignedExcelParticipationReport(data: ExcelParticipationReportData): Promise<any> {
    try {
      return await apiClient.post("/institution/assigned-participation-report/excel", data);
    } catch (error) {
      logger.error("Error fetching assigned excel participation report:", error);
      throw error;
    }
  }

  /**
   * Get assessment report metadata by ID (Institution)
   */
  async getAssessmentReportsforId(id: string, filters: any = {}): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
      return await apiClient.get(`/institution/reports/assessment/${id}${qs}`);
    } catch (error) {
      logger.error("Error fetching institution assessment report info:", error);
      throw error;
    }
  }

  /**
   * Get assessment report data (Institution)
   */
  async getAssessmentReportData(id: string, filters: any = {}): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
      return await apiClient.get(`/institution/reports/assessment-report-data/${id}${qs}`);
    } catch (error) {
      logger.error("Error fetching institution assessment report data:", error);
      throw error;
    }
  }

  async getAssessmentReportFullData(id: string, filters: any = {}): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : "";
      return await apiClient.get(`/institution/reports/assessment-report-full-data/${id}${qs}`);
    } catch (error) {
      logger.error("Error fetching institution assessment report full data:", error);
      throw error;
    }
  }

  async getAssessmentReportTabData(id: string, tab: string, filters: any = {}): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      if (filters.batch_year_id) queryParams.append("batch_year_id", filters.batch_year_id);
      if (filters.batch_id) queryParams.append("batch_id", filters.batch_id);
      if (filters.learning_path_uuid) queryParams.append("learning_path_uuid", filters.learning_path_uuid);
      if (filters.topic_uuid) queryParams.append("topic_uuid", filters.topic_uuid);
      if (filters.content_uuid) queryParams.append("content_uuid", filters.content_uuid);

      const queryString = queryParams.toString();
      
      const tabEndpointMap: Record<string, string> = {
        overview: "overview",
        summary: "summary",
        summary_report: "summary",
        batch_wise: "batch-wise",
        students: "attempted-list",
        attempted_list: "attempted-list",
        questions: "questions-wise",
        questions_wise: "questions-wise",
        modules: "module-wise",
        modules_wise: "module-wise",
        participation: "participation",
      };

      const endpoint = tabEndpointMap[tab] || "overview";
      const url = `/institution/reports/course-assessment/${endpoint}/${id}${queryString ? `?${queryString}` : ""}`;
      return await apiClient.get(url);
    } catch (error) {
      logger.error(`Error fetching institution assessment report data for tab ${tab}:`, error);
      throw error;
    }
  }

  /**
   * Get individual assessment report for a specific student and exam (Institution)
   */
  async getAssessmentAttemptReport(examId: string, userId: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/reports/individual-student-report?exam_id=${examId}&user_id=${userId}`);
    } catch (error) {
      logger.error("Error fetching institution individual student assessment report:", error);
      throw error;
    }
  }

  async getIndividualStudentReport(exam_id: string, user_id: string): Promise<any> {
    return this.getAssessmentAttemptReport(exam_id, user_id);
  }


  /**
   * Get live assessment users count (Institution)
   */
  async getAssessmentLiveCount(assessmentId: string): Promise<{ success: number; message: string; count: number }> {
    try {
      return await apiClient.get(`/institution/assessment/live-count/${assessmentId}`);
    } catch (error) {
      logger.error("Error fetching institution assessment live count:", error);
      throw error;
    }
  }

  /**
   * Download assessment PDF report for a specific attempt (Institution)
   */
  async downloadAssessmentReportPDF(attemptId: string): Promise<any> {
    try {
      const blob = await apiClient.getBlob(`/institution/reports/assessment-report-pdf/${attemptId}`);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Assessment_Report_${attemptId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      logger.error("Error downloading institution assessment PDF report:", error);
      throw error;
    }
  }

  /**
   * Force submit all in-progress assessment attempts (Institution)
   * TODO: Implement actual API call when backend is ready
   * Endpoint: POST /institution/assessment/:id/force-submit
   */
  async forceSubmitAssessmentAttempts(assessmentId: string): Promise<any> {
    // Placeholder for API call
    logger.debug('Force submit attempts for assessment:', assessmentId);
    return { success: 1, message: 'Not implemented yet' };
  }

  /**
   * Auto submit all in-progress attempts for an assessment (Institution)
   * Endpoint: POST /institution/assessment/auto_submit_attempts
   */
  async autoSubmitAttempts(examId: string): Promise<any> {
    try {
      logger.debug("Institution Service: Auto submit attempts", { exam_id: examId });
      return await apiClient.post(`/institution/assessment/auto_submit_attempts`, {
        exam_id: examId,
      });
    } catch (error) {
      logger.error("Error auto-submitting in-progress attempts:", error);
      throw error;
    }
  }

  /**
   * Update attempt result (time spent / tab switches) for a given exam + user (Institution)
   * Endpoint: POST /institution/assessment/attempt/resume
   */
  async updateAttemptResult(payload: {
    user_id: string;
    attempt_id: string;
    duration: number | string;
    tab_swithces: number | string;
    exam_id: string;
  }): Promise<any> {
    try {
      logger.debug("Institution Service: Resuming attempt", payload);
      return await apiClient.post(
        "/institution/assessment/attempt/resume",
        payload,
      );
    } catch (error) {
      logger.error("Error resuming attempt:", error);
      throw error;
    }
  }

  /**
   * Update per-section time spent + status for a given attempt (Institution)
   * Endpoint: POST /institution/assessment/attempt/section_resume
   */
  async updateAttemptSectionResume(payload: {
    user_id: string;
    section_id: string;
    duration: number | string;
    status?: string;
    attempt_id: string;
  }): Promise<any> {
    try {
      logger.debug("Institution Service: Updating section resume", payload);
      return await apiClient.post(
        "/institution/assessment/attempt/section_resume",
        payload,
      );
    } catch (error) {
      logger.error("Error updating section resume:", error);
      throw error;
    }
  }

  /**
   * Get attempts for a specific exam + user (Institution)
   * Endpoint: GET /institution/assessment-report/:id/:user_id
   */
  async getExamReports(examId: string, userId: string): Promise<any> {
    try {
      return await apiClient.get(
        `/institution/assessment-report/${examId}/${userId}`,
      );
    } catch (error) {
      logger.error("Error fetching exam reports for user:", error);
      throw error;
    }
  }

  /**
   * Get attempt solutions + user details (Institution)
   * Endpoint: GET /institution/assessment/solutions/:attempt_id
   */
  async getAttemptSolutions(attemptId: string): Promise<any> {
    try {
      return await apiClient.get(
        `/institution/assessment/solutions/${attemptId}`,
      );
    } catch (error) {
      logger.error("Error fetching attempt solutions:", error);
      throw error;
    }
  }

  /**
   * Get batch years for institution (Institution)
   * Endpoint: GET /institution/batch_year/list
   */
  async getBatchYears(institutionId?: string): Promise<any> {
    try {
      const url = institutionId
        ? `/institution/batch_year/list?institution_id=${institutionId}`
        : "/institution/batch_year/list";
      return await apiClient.get(url);
    } catch (error) {
      logger.error("Error fetching batch years:", error);
      throw error;
    }
  }

  /**
   * Download assessment report as XLSX (Institution)
   * TODO: Implement actual API call when backend is ready
   * Endpoint: GET /institution/assessment-report/:id/excel/download
   */
  async downloadAssessmentReportXLSX(assessmentId: string): Promise<any> {
    try {
      const blob = await apiClient.getBlob(
        `/institution/assessment-report/${assessmentId}/excel/download`,
      );

      const safeId = String(assessmentId).replace(/[^a-zA-Z0-9_-]/g, "_");
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Assessment_Report_${safeId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: 1, message: "Excel report downloaded successfully" };
    } catch (error) {
      logger.error("Error downloading assessment XLSX report:", error);
      throw error;
    }
  }

  /**
   * Get aggregate summary report for multiple assessments (Institution)
   */
  async getSummaryReport(ids: string[] | string, filters: any = {}): Promise<any> {
    try {
      return await apiClient.post(`/institution/reports/summary-report`, { ids, ...filters });
    } catch (error) {
      logger.error("Error fetching institution summary report:", error);
      throw error;
    }
  }

  /**
   * Download summary report as PDF (Institution)
   */
  async downloadSummaryReportPDF(ids: string[] | string, filters: any = {}): Promise<any> {
    try {
      const blob = await apiClient.postBlob(`/institution/reports/summary-report-pdf`, { ids, ...filters });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().split("T")[0];
      link.download = `Summary_Report_${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: 1, message: "PDF downloaded successfully" };
    } catch (error) {
      logger.error("Error downloading summary report PDF:", error);
      throw error;
    }
  }

  /**
   * Get detailed participation report (Institution)
   */
  async getParticipationReport(data: any): Promise<any> {
    try {
      if (data.download === true) {
        const blob = await apiClient.postBlob(`/institution/reports/participation-report`, data);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const examName = "Participation_Report";
        const date = new Date().toISOString().split("T")[0];
        link.download = `${examName}_${date}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: 1, message: "File downloaded successfully" };
      } else {
        return await apiClient.post(`/institution/reports/participation-report`, data);
      }
    } catch (error) {
      logger.error("Error fetching institution participation report:", error);
      throw error;
    }
  }

  /**
   * Get report for selected assessments (Institution)
   */
  async getMultiAssessmentReports(ids: number[], filters: any = {}): Promise<any> {
    try {
      const payload = {
        ids,
        batch_year_id: filters.batch_year_id,
        batch_id: filters.batch_id,
      };

      return await apiClient.post(`/institution/reports/multi-assessment-report-data`, payload);
    } catch (error) {
      logger.error("Error fetching multi-assessment reports:", error);
      throw error;
    }
  }

  /**
   * Download multi-assessment Excel report (Institution)
   */
  async getMultiAssessmentExcelReport(data: any): Promise<any> {
    try {
      if (data.download === true) {
        const blob = await apiClient.postBlob("/institution/reports/multi-assessment-excel", data);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const date = new Date().toISOString().split("T")[0];
        link.download = `Multi_Assessment_Report_${date}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: 1, message: "File downloaded successfully" };
      } else {
        return await apiClient.post("/institution/reports/multi-assessment-excel", data);
      }
    } catch (error) {
      logger.error("Error fetching multi-assessment Excel report:", error);
      throw error;
    }
  }

  /**
   * Download assessment Excel report (Institution)
   */
  async getExcelAssessmentReport(data: any): Promise<any> {
    try {
      if (data.download === true) {
        const blob = await apiClient.postBlob(`/institution/reports/assessment-excel`, data);

        // Create download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Generate filename
        const examName = "Assessment_Report";
        const date = new Date().toISOString().split("T")[0];
        link.download = `${examName}_${date}.xlsx`;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true, message: "File downloaded successfully" };
      } else {
        return await apiClient.post(`/institution/reports/assessment-excel`, data);
      }
    } catch (error) {
      logger.error("Error downloading institution assessment Excel report:", error);
      throw error;
    }
  }

  /**
   * Get user activity logs (Institution)
   */
  async getUserLogs(userId: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/user/${userId}/logs`);
    } catch (error) {
      logger.error("Error fetching user logs:", error);
      return { success: 0, data: [] }; // Return empty data gracefully
    }
  }

  // ============================================
  // VERSANT ACCESS MANAGEMENT METHODS
  // ============================================

  /**
   * Get assigned Versant categories for the institution
   */
  async getVersantAccessCategories(): Promise<{
    success: number;
    data: Array<{
      versant_category_id: string;
      exam_ids: string | null;
      user_id: string;
      user_role: string;
      category_name: string;
      exam_count: number;
    }>;
  }> {
    try {
      return await apiClient.get("/institution/versant/categories");
    } catch (error) {
      logger.error("Error fetching Versant access categories:", error);
      throw error;
    }
  }

  /**
   * Get assigned Versant assessments for the institution
   */
  async getVersantAccessAssessments(): Promise<{
    success: number;
    data: Array<{
      id: string;
      name: string;
      versant_category_id: string;
      category: string;
      attempts: number;
    }>;
  }> {
    try {
      return await apiClient.get("/institution/versant/assessments");
    } catch (error) {
      logger.error("Error fetching Versant access assessments:", error);
      throw error;
    }
  }

  /**
   * Create Versant category access for a batch
   */
  async createVersantCategoryAccess(data: {
    batch_year: string;
    category_id: string;
    batches: string[];
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/category-access/create", data);
    } catch (error) {
      logger.error("Error creating Versant category access:", error);
      throw error;
    }
  }

  /**
   * Get category access details (batches) for a category
   */
  async getVersantCategoryAccess(categoryId: string): Promise<{
    success: number;
    category: string;
    data: Array<{
      id: number;
      institution_id: string;
      batch_year_id: string;
      versant_category_id: string;
      batch_id: string[] | string;
      user_id: string;
      user_role: string;
      created_at: string;
      updated_at: string;
      batch_year_name: string;
      institution: string;
      all_batches: any[];
      batches: Array<{ batch_id: string; batch_name: string }>;
    }>;
  }> {
    try {
      return await apiClient.get(`/institution/versant/category-access/${categoryId}`);
    } catch (error) {
      logger.error("Error fetching Versant category access:", error);
      throw error;
    }
  }

  /**
   * Update Versant category access
   */
  async updateVersantCategoryAccess(data: {
    id: number;
    batch_year: string;
    category_id: string;
    batches: string[];
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/category-access/update", data);
    } catch (error) {
      logger.error("Error updating Versant category access:", error);
      throw error;
    }
  }

  /**
   * Delete Versant category access
   */
  async deleteVersantCategoryAccess(data: {
    id: number;
    category_id: string;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/category-access/delete", data);
    } catch (error) {
      logger.error("Error deleting Versant category access:", error);
      throw error;
    }
  }

  /**
   * Get Versant assessment access details for a specific exam
   */
  async getVersantAssessmentAccess(categoryId: string, examId: string): Promise<{
    success: number;
    exam: { name: string; category: string };
    data: Array<{
      id: number;
      institution_id: string;
      batch_year_id: string;
      batch_year_name: string;
      institution: string;
      batch_id: string;
      batch_name: string;
      exam_id: string;
      access_id: number | null;
      access: number;
      from: string | null;
      to: string | null;
      fromTime: string | null;
      toTime: string | null;
      user_level: number;
      freeze_test: number;
      auto_submit: number | null;
      auto_submit_time: string | null;
    }>;
  }> {
    try {
      return await apiClient.get(`/institution/versant/assessment-access/${categoryId}/${examId}`);
    } catch (error) {
      logger.error("Error fetching Versant assessment access:", error);
      throw error;
    }
  }

  /**
   * Update Versant assessment access for a batch
   */
  async updateVersantAssessmentAccess(data: {
    exam_id: string;
    batch_year: string;
    batch: string;
    access: boolean | number;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/update", data);
    } catch (error) {
      logger.error("Error updating Versant assessment access:", error);
      throw error;
    }
  }

  /**
   * Update Versant assessment schedule / time selection for a batch
   */
  async updateVersantAssessmentTime(data: {
    id: number;
    from?: string | null;
    to?: string | null;
    fromTime?: string | null;
    toTime?: string | null;
    auto_submit?: 0 | 1;
    auto_submit_time?: string | number | null;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/time-selection", data);
    } catch (error) {
      logger.error("Error updating Versant assessment time selection:", error);
      throw error;
    }
  }

  /**
   * Update Versant assessment schedule globally for all batches
   */
  async updateVersantGlobalAccessTime(data: {
    exam_id: string;
    from?: string | null;
    to?: string | null;
    fromTime?: string | null;
    toTime?: string | null;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/global-time", data);
    } catch (error) {
      logger.error("Error updating Versant global access time:", error);
      throw error;
    }
  }

  /**
   * Bulk grant Versant assessment access to all batches
   */
  async grantVersantExamAccessToAll(data: {
    exam_id: string;
    batches: Array<{ batch: string; batch_year: string }>;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/grant-all", data);
    } catch (error) {
      logger.error("Error granting Versant assessment access to all:", error);
      throw error;
    }
  }

  /**
   * Update Versant user level access flag
   */
  async updateVersantExamUserLevelAccess(data: {
    checked: 0 | 1;
    access_id: number;
    batch: string;
    exam_id: string;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/user-level/update", data);
    } catch (error) {
      logger.error("Error updating Versant user level access:", error);
      throw error;
    }
  }

  /**
   * Get students in a batch with their access state for Versant exam
   */
  async getVersantExamAccessUsersList(data: {
    exam_id: string;
    batch_id: string;
  }): Promise<{ success: number; data: any[]; message?: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/users/list", data);
    } catch (error) {
      logger.error("Error fetching Versant exam access users list:", error);
      throw error;
    }
  }

  /**
   * Toggle student access for Versant exam
   */
  async changeVersantUserAccessForExam(data: {
    exam_id: string;
    user_id: string;
    batch_id: string;
    checked: 0 | 1;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/user/toggle", data);
    } catch (error) {
      logger.error("Error changing Versant user access:", error);
      throw error;
    }
  }

  /**
   * Grant Versant assessment access to users by email or regd numbers
   */
  async grantVersantEmailAccess(data: {
    emails: string;
    isEmail?: number;
    batch: string;
    exam_id: string;
    access_id?: number | null;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/grant-email", data);
    } catch (error) {
      logger.error("Error granting Versant email access:", error);
      throw error;
    }
  }

  /**
   * Toggle freeze status for Versant exam
   */
  async freezeVersantTest(data: {
    exam_id: string;
    batch_year: string;
    batch: string;
    freeze: boolean | number;
  }): Promise<{ success: number; message: string }> {
    try {
      return await apiClient.post("/institution/versant/assessment-access/freeze-test", data);
    } catch (error) {
      logger.error("Error freezing/unfreezing Versant test:", error);
      throw error;
    }
  }

  // ===== VERSANT REPORT OPERATIONS =====

  async getVersantReportsByID(
    id: string,
    userId: string,
  ): Promise<ApiResponse<any>> {
    try {
      return await apiClient.post(`/institution/versant/reports/${id}`, {
        user_id: userId,
      });
    } catch (error) {
      logger.error("Error fetching Versant report by ID:", error);
      throw error;
    }
  }

  async getUserVersantReport(
    examId: string,
    userId: string,
  ): Promise<ApiResponse<any>> {
    try {
      return await apiClient.get(`/institution/versant/reports/${examId}/${userId}`);
    } catch (error) {
      logger.error("Error fetching user Versant report:", error);
      throw error;
    }
  }

  async getVersantOverallReport(attemptId: string): Promise<ApiResponse<any>> {
    try {
      return await apiClient.get(`/institution/versant/reports/overall/${attemptId}`);
    } catch (error) {
      logger.error("Error fetching Versant overall report:", error);
      throw error;
    }
  }

  async getVersantSolutions(attemptId: string): Promise<ApiResponse<any>> {
    try {
      return await apiClient.get(`/institution/versant/reports/solutions/${attemptId}`);
    } catch (error) {
      logger.error("Error fetching Versant solutions:", error);
      throw error;
    }
  }

  async getExcelVersantReport(data: any): Promise<ApiResponse<any>> {
    try {
      return await apiClient.post("/institution/versant/reports/excel", data);
    } catch (error) {
      logger.error("Error exporting Excel Versant report:", error);
      throw error;
    }
  }
  async getCodespacesListByUser(userId: string): Promise<any> {
    try {
      return await apiClient.post("/institution/codespace/list", { user_id: userId });
    } catch (error) {
      logger.error("Error fetching codespaces:", error);
      throw error;
    }
  }

  async getCodespaceProject(userId: string, folderId: string): Promise<any> {
    try {
      return await apiClient.post("/institution/codespace/project", { user_id: userId, folder_id: folderId });
    } catch (error) {
      logger.error("Error fetching project details:", error);
      throw error;
    }
  }

  /**
   * CODE STUDIO ACCESS METHODS
   */
  async getCodeStudioAccess(): Promise<{
    success: number;
    message?: string;
    data?: {
      creditSummary: {
        total_hours: number;
        used_hours: number;
        remaining_hours: number;
      };
      batchYears: Array<{
        batch_year_id: string;
        batch_year_name: string;
        access: number;
        user_level_access: number;
        user_ids: string;
        purchased_hours: number;
        remaining_hours: number;
        batches: Array<{
          batch_id: string;
          batch_name: string;
          has_access: boolean;
          user_level_access?: number | null;
          user_max_usage?: number | null;
          purchased_hours?: number | null;
          used_hours?: number | null;
          remaining_hours?: number | null;
        }>;
      }>;
    };
  }> {
    try {
      return await apiClient.get("/institution/codestudio/access");
    } catch (error) {
      logger.error("Error fetching CodeStudio access:", error);
      throw error;
    }
  }

  async assignCodeStudioAccess(data: {
    batch_year_id: string;
    batch_id: string;
    access: number;
  }): Promise<{ success: number; message?: string }> {
    try {
      return await apiClient.post("/institution/codestudio/access", data);
    } catch (error) {
      logger.error("Error assigning CodeStudio access:", error);
      throw error;
    }
  }

  async toggleCodeStudioUserLevelAccess(data: {
    batch_year_id: string;
    batch_id: string;
  }): Promise<{ success: number; message?: string }> {
    try {
      return await apiClient.post("/institution/codestudio/access/toggle-user-level", data);
    } catch (error) {
      logger.error("Error toggling CodeStudio user level access:", error);
      throw error;
    }
  }

  async toggleCodeStudioUserAccess(data: {
    batch_year_id: string;
    batch_id: string;
    user_id: string;
    access: number;
  }): Promise<{ success: number; message?: string }> {
    try {
      return await apiClient.post("/institution/codestudio/access/toggle-user", data);
    } catch (error) {
      logger.error("Error toggling CodeStudio user access:", error);
      throw error;
    }
  }

  async getCodeStudioAccessUsersList(data: {
    batch_year_id: string;
    batch_id: string;
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post(
        "/institution/codestudio/access/users/list",
        data,
      );
    } catch (error) {
      logger.error("Error fetching CodeStudio access users list:", error);
      throw error;
    }
  }

  async grantUserLevelAccess(data: {
    batch_year_id: string;
    batch_id: string;
    data_type: "email" | "hallticket";
    list: string[];
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post(
        "/institution/codestudio/access/grant-user-level",
        data,
      );
    } catch (error) {
      logger.error("Error granting CodeStudio user level access:", error);
      throw error;
    }
  }

  async assignCodeStudioCredits(data: {
    batch_id: string;
    credits: number;
  }): Promise<ApiResponse> {
    try {
      return await apiClient.post(
        "/institution/codestudio/access/assigncredits",
        data,
      );
    } catch (error) {
      logger.error("Error assigning CodeStudio credits:", error);
      throw error;
    }
  }

  async getCodeStudioBatchCredits(batchId: string): Promise<{
    success: number;
    data: Array<{
      id: number;
      purchased_hours: number;
      remaining_hours: number;
      used_hours: number;
      creator_name: string;
      created_at: string;
    }>;
    total: number;
  }> {
    try {
      return await apiClient.post("/institution/codestudio/access/batch-credits", {
        batch_id: batchId,
      });
    } catch (error) {
      logger.error("Error fetching batch credits:", error);
      throw error;
    }
  }

  async updateCodestudioBatchSettings(batchId: string, data: { batch_id: string; user_max_usage: number }) {
    try {
      return await apiClient.post("/institution/codestudio/access/user-max-usage", data);
    } catch (error) {
      logger.error("Error updating batch settings:", error);
      throw error;
    }
  }

  private _dashboard_overview_promise: Promise<any> | null = null;
  private _courses_dashboard_promise: Promise<any> | null = null;
  private _passout_years_full_promise: Promise<any> | null = null;
  private _student_growth_promise: Promise<any> | null = null;
  private _kpi_comparison_map = new Map<string, Promise<any>>();
  private _coding_submissions_map = new Map<string, Promise<any>>();

  /**
   * Get Institution Dashboard Overview (BFF)
   */
  async getDashboardOverview(): Promise<{
    success: number;
    message?: string;
    data?: {
      institution_name: string;
      total_students: number;
      verified_students: number;
      total_batches: number;
      total_passout_years: number;
      trainings_conducted: number;
      courses_assigned: number;
      exams_conducted: number;
      versant_conducted: number;
      practice_sessions: number;
      exams_attempted: number;
      learning_time: number;
      questions_attempted: number;
      coding_submissions: number;
    };
  }> {
    if (this._dashboard_overview_promise) {
      return this._dashboard_overview_promise;
    }
    try {
      this._dashboard_overview_promise = apiClient.get("/institution/dashboard/overview");
      const response = await this._dashboard_overview_promise;
      setTimeout(() => {
        this._dashboard_overview_promise = null;
      }, 10000);
      return response;
    } catch (error) {
      this._dashboard_overview_promise = null;
      logger.error("Error fetching institution dashboard overview:", error);
      throw error;
    }
  }

  /**
   * Get KPI summary - flat clean counts for Section 1 KPI cards
   */
  async getKpiSummary(): Promise<{
    success: number;
    message: string;
    data?: {
      institution_name: string;
      total_students: number;
      total_batches: number;
      courses_assigned: number;
      trainings_conducted: number;
      exams_conducted: number;
      practice_sessions: number;
      versant_assessments: number;
      exams_attempted: number;
      learning_time: number;
      questions_attempted: number;
      coding_submissions: number;
      coding_testcases: number;
      platform_health_score: number;
      passout_year_count: number;
    };
  }> {
    try {
      return await apiClient.get("/institution/analytics/kpi");
    } catch (error) {
      logger.error("Error fetching KPI summary:", error);
      throw error;
    }
  }

  /**
   * Get KPI Comparison between two dates with optional filters (deduplicated)
   */
  async getKpiComparison(
    date1?: string,
    date2?: string,
    passoutYear?: string,
    department?: string,
    batchId?: string
  ): Promise<{
    success: number;
    data?: {
      date1: string;
      date2: string;
      metrics: Array<{
        label: string;
        val1: number;
        val2: number;
      }>;
    };
  }> {
    const key = `${date1 || ""}_${date2 || ""}_${passoutYear || ""}_${department || ""}_${batchId || ""}`;
    if (this._kpi_comparison_map.has(key)) {
      return this._kpi_comparison_map.get(key)!;
    }
    try {
      const params = new URLSearchParams();
      if (date1) params.append("date1", date1);
      if (date2) params.append("date2", date2);
      if (passoutYear) params.append("passoutYear", passoutYear);
      if (department) params.append("department", department);
      if (batchId) params.append("batchId", batchId);

      const qs = params.toString();
      const endpoint = qs ? `/institution/analytics/kpi-comparison?${qs}` : `/institution/analytics/kpi-comparison`;

      const promise = apiClient.get(endpoint);
      this._kpi_comparison_map.set(key, promise);
      const response = await promise;
      setTimeout(() => {
        this._kpi_comparison_map.delete(key);
      }, 10000);
      return response;
    } catch (error) {
      this._kpi_comparison_map.delete(key);
      logger.error("Error fetching KPI comparison:", error);
      throw error;
    }
  }

  /**
   * Get courses dashboard analytics (distribution, batch coverage, top courses, funnel, matrix) - deduplicated
   */
  async getCoursesDashboardAnalytics(): Promise<{
    success: number;
    data?: any;
  }> {
    if (this._courses_dashboard_promise) {
      return this._courses_dashboard_promise;
    }
    try {
      this._courses_dashboard_promise = apiClient.get('/institution/analytics/courses-dashboard');
      const response = await this._courses_dashboard_promise;
      setTimeout(() => {
        this._courses_dashboard_promise = null;
      }, 10000);
      return response;
    } catch (error) {
      this._courses_dashboard_promise = null;
      logger.error("Error fetching courses dashboard analytics:", error);
      throw error;
    }
  }

  /**
   * Get student growth analytics - passout years with batch/student counts + department enrollment
   */
  async getStudentGrowthAnalytics(): Promise<{
    success: number;
    data: {
      passout_years: Array<{
        year: string;
        total: number;
        active: number;
        batches: number;
        departments_count: number;
        departments: Array<{
          name: string;
          total: number;
          active: number;
          batches: number;
        }>;
      }>;
      department_enrollment: Array<{
        department: string;
        students: number;
      }>;
    };
  }> {
    if (this._student_growth_promise) {
      return this._student_growth_promise;
    }
    try {
      this._student_growth_promise = apiClient.get("/institution/analytics/student");
      const response = await this._student_growth_promise;
      setTimeout(() => {
        this._student_growth_promise = null;
      }, 10000);
      return response;
    } catch (error) {
      this._student_growth_promise = null;
      logger.error("Error fetching student growth analytics:", error);
      throw error;
    }
  }

  /**
   * Get department breakdown by year analytics
   */
  async getDepartmentBreakdownAnalytics(): Promise<{
    success: number;
    data: Array<{
      year: string;
      [key: string]: string | number;
    }>;
  }> {
    try {
      return await apiClient.get("/institution/analytics/department-breakdown");
    } catch (error) {
      logger.error("Error fetching department breakdown analytics:", error);
      throw error;
    }
  }
  async getExamAnalytics(passoutYearId?: string, batchId?: string): Promise<{
    success: number;
    data: {
      score_trends: Array<{
        month: string;
        submissions: number;
        avgScore: number;
        passRate: number;
      }>;
      subject_performance: Array<{
        subject: string;
        avgScore: number;
      }>;
    };
  }> {
    try {
      const params = new URLSearchParams();
      if (passoutYearId && passoutYearId !== 'all') params.append('passoutYearId', passoutYearId);
      if (batchId && batchId !== 'all') params.append('batchId', batchId);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiClient.get(`/institution/analytics/exam${queryString}`);
    } catch (error) {
      logger.error("Error fetching exam performance analytics:", error);
      throw error;
    }
  }

  /**
   * Get Coding Submissions Analytics (deduplicated)
   */
  async getCodingSubmissionsAnalytics(passoutYearId?: string, batchId?: string): Promise<{
    success: number;
    data: Array<{
      month: string;
      submissions: number;
      success: number;
    }>;
  }> {
    const key = `${passoutYearId || "all"}_${batchId || "all"}`;
    if (this._coding_submissions_map.has(key)) {
      return this._coding_submissions_map.get(key)!;
    }
    try {
      const params = new URLSearchParams();
      if (passoutYearId && passoutYearId !== 'all') params.append('passoutYearId', passoutYearId);
      if (batchId && batchId !== 'all') params.append('batchId', batchId);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const promise = apiClient.get(`/institution/analytics/coding-submissions${queryString}`);
      this._coding_submissions_map.set(key, promise);
      const response = await promise;
      setTimeout(() => {
        this._coding_submissions_map.delete(key);
      }, 10000);
      return response;
    } catch (error: any) {
      this._coding_submissions_map.delete(key);
      logger.error('Error fetching coding submissions analytics:', error);
      throw error;
    }
  }

  /**
   * Get Question Bank Analytics
   */
  async getQuestionBankAnalytics(): Promise<{
    success: number;
    data: Array<{
      name: string;
      value: number;
    }>;
  }> {
    try {
      return await apiClient.get('/institution/analytics/question-bank');
    } catch (error: any) {
      logger.error('Error fetching question bank analytics:', error);
      throw error;
    }
  }

  /**
   * Get Course Engagement & Leaderboard Analytics
   */
  async getCourseAssignmentDistribution(): Promise<{
    success: number;
    message?: string;
    data?: Array<{
      year: string;
      total_courses: number;
      batches: Array<{
        name: string;
        courses_count: number;
      }>;
    }>;
  }> {
    try {
      const response = await apiClient.get('/institution/analytics/course-assignments');
      return response;
    } catch (error: any) {
      logger.error('Error fetching course assignment distribution:', error);
      throw error;
    }
  }

  async getEngagementAnalytics(): Promise<{
    success: number;
    data: {
      top_courses: Array<{
        course: string;
        rating: number;
        enrolled: number;
        completed: number;
        rate: number;
      }>;
      top_performers: Array<{
        name: string;
        department: string;
        exams: number;
        avgScore: number;
      }>;
    };
  }> {
    try {
      return await apiClient.get('/institution/analytics/engagement');
    } catch (error: any) {
      logger.error('Error fetching engagement analytics:', error);
      throw error;
    }
  }

  /**
   * Get Batch Health Monitoring Analytics
   */
  async getBatchHealthAnalytics(passoutYear?: string): Promise<{
    success: number;
    data: Array<{
      id: string;
      name: string;
      department: string;
      passoutYear: string;
      students: number;
      activeStudents: number;
      activityRate: number;
      exams: number;
      courses: number;
      versant: number;
      versantExams: number;
      trainings: number;
      practice: number;
      practiceTests: number;
      exams_attempted: number;
      questions_attempted: number;
      coding_submissions: number;
      coding_testcases: number;
      examPassRate: number;
      practiceAccuracy: number;
      versantScore: number;
      courseCompletion: number;
      healthScore: number;
      riskStatus: string;
    }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (passoutYear && passoutYear !== 'All Years') {
        params.append('passoutYear', passoutYear);
      }
      return await apiClient.get(`/institution/analytics/batch-health?${params.toString()}`);
    } catch (error: any) {
      logger.error('Error fetching batch health analytics:', error);
      throw error;
    }
  }

  async getComparisonAnalytics(passoutYearId?: string, batchId?: string): Promise<{
    success: number;
    data: {
      radarData: Array<{
        metric: string;
        [key: string]: string | number; // For dynamic department keys like CSE: 80, IT: 75
      }>;
      batchComparisonData: Array<{
        batch: string;
        avgScore: number;
        passRate: number;
        engagement: number;
        students: number;
      }>;
    };
  }> {
    try {
      let url = "/institution/analytics/comparison";
      const params = new URLSearchParams();
      if (passoutYearId && passoutYearId !== "all") params.append("passoutYearId", passoutYearId);
      if (batchId && batchId !== "all") params.append("batchId", batchId);

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      return await apiClient.get(url);
    } catch (error) {
      logger.error("Error fetching comparison analytics:", error);
      throw error;
    }
  }
  async getPassoutYearsWithBatches(): Promise<{
    success: number;
    message: string;
    total_passout_years: number;
    total_batches: number;
    total_students: number;
    data: Array<{
      uuid: string;
      name: string;
      status: number;
      created_at: string;
      updated_at: string;
      batches_count: number;
      batches: Array<{
        uuid: string;
        name: string;
        status: number;
        batch_tag_name: string;
        batch_tag_uuid: string;
        batch_year_name: string;
        batch_created_at: string;
        batch_updated_at: string;
        students: number;
        activeStudents: number;
        exams: number;
        courses: number;
        versant: number;
        versantExams: number;
        trainings: number;
        practice: number;
        practiceTests: number;
        exams_attempted: number;
        questions_attempted: number;
        coding_submissions: number;
        coding_testcases: number;
        courseCompletion: number;
        examPassRate: number;
        practiceAccuracy: number;
        versantScore: number;
        healthScore: number;
        riskStatus: string;
      }>;
    }>;
  }> {
    if (this._passout_years_full_promise) {
      return this._passout_years_full_promise;
    }
    try {
      this._passout_years_full_promise = apiClient.get('/institution/passout-years/full');
      const response = await this._passout_years_full_promise;
      setTimeout(() => {
        this._passout_years_full_promise = null;
      }, 10000);
      return response;
    } catch (error) {
      this._passout_years_full_promise = null;
      logger.error('Error fetching passout years with batches:', error);
      throw error;
    }
  }

  // ── Module Analytics ─────────────────────────────────────────────────────────
  async getModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      exams: {
        total_exams: number;
        average_score: number;
        pass_rate: number;
        participation_rate: number;
        trend: Array<{ date: string; value: number }>;
        top_exams: Array<{ name: string; score: number }>;
      };
      courses: {
        enrollments: number;
        completion_rate: number;
        new_this_month: number;
        top_courses: Array<{ name: string; enrollment: number; completion: number }>;
      };
      trainings: {
        attendance_rate: number;
        completion_rate: number;
        feedback_score: number;
        trend: Array<{ date: string; value: number }>;
      };
      practice: {
        sessions_count: number;
        accuracy_rate: number;
        questions_attempted: number;
        topic_performance: Array<{ topic: string; accuracy: number }>;
        trend: Array<{ date: string; value: number }>;
      };
      versant: {
        listening: number;
        speaking: number;
        reading: number;
        writing: number;
        assessments: number;
        students_assessed: number;
        improvement: number;
        score_distribution: Array<{ name: string; value: number; color: string }>;
      };
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/module', params || {});
    } catch (error) {
      logger.error('Error fetching module analytics:', error);
      throw error;
    }
  }

  async getExamsModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      total_exams: number;
      average_score: number;
      pass_rate: number;
      participation_rate: number;
      trend: Array<{ date: string; value: number }>;
      top_exams: Array<{ name: string; score: number }>;
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/exams', params || {});
    } catch (error) {
      logger.error('Error fetching exams module analytics:', error);
      throw error;
    }
  }

  async getCoursesModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      enrollments: number;
      completion_rate: number;
      new_this_month: number;
      top_courses: Array<{ name: string; enrollment: number; completion: number }>;
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/courses', params || {});
    } catch (error) {
      logger.error('Error fetching courses module analytics:', error);
      throw error;
    }
  }

  async getTrainingsModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      attendance_rate: number;
      completion_rate: number;
      feedback_score: number;
      trend: Array<{ date: string; value: number }>;
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/trainings', params || {});
    } catch (error) {
      logger.error('Error fetching trainings module analytics:', error);
      throw error;
    }
  }

  async getPracticeModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      sessions_count: number;
      accuracy_rate: number;
      questions_attempted: number;
      topic_performance: Array<{ topic: string; accuracy: number }>;
      trend: Array<{ date: string; value: number }>;
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/practice', params || {});
    } catch (error) {
      logger.error('Error fetching practice module analytics:', error);
      throw error;
    }
  }

  async getVersantModuleAnalytics(params?: {
    passout_year_id?: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: number;
    data: {
      listening: number;
      speaking: number;
      reading: number;
      writing: number;
      assessments: number;
      students_assessed: number;
      improvement: number;
      score_distribution: Array<{ name: string; value: number; color: string }>;
    };
  }> {
    try {
      return await apiClient.post('/institution/analytics/versant', params || {});
    } catch (error) {
      logger.error('Error fetching versant module analytics:', error);
      throw error;
    }
  }

  async getBasicDetails(): Promise<any> {
    try {
      return await apiClient.get("/institution/basic-details");
    } catch (error) {
      logger.error("Error fetching institution basic details:", error);
      throw error;
    }
  }

  async getAccessPeriodsForUpdateUser(): Promise<any> {
    try {
      return await apiClient.get("/institution/access-periods/update_user");
    } catch (error) {
      logger.error("Error fetching access periods for user update:", error);
      throw error;
    }
  }

  async updateUserAccessPeriod(data: { user_id: string; access_period: string }): Promise<any> {
    try {
      return await apiClient.post("/institution/user/update_access_period", data);
    } catch (error) {
      logger.error("Error updating user access period:", error);
      throw error;
    }
  }

  async updateMultipleUsersAccessPeriod(data: { access_period: string; user_ids: string[] }): Promise<any> {
    try {
      return await apiClient.post("/institution/users/update_access_period", data);
    } catch (error) {
      logger.error("Error updating multiple users access period:", error);
      throw error;
    }
  }

  async extendUsersByBatch(data: { batch_ids: string[]; access_period: string }): Promise<any> {
    try {
      return await apiClient.post("/institution/batches/extend_access", data);
    } catch (error) {
      logger.error("Error extending users by batch:", error);
      throw error;
    }
  }

  async getUserPracticeSummary(user_id: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/user/${user_id}/practice-summary`);
    } catch (error) {
      logger.error("Error fetching user practice summary:", error);
      throw error;
    }
  }

  async getUserVersantSummary(user_id: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/user/${user_id}/versant-summary`);
    } catch (error) {
      logger.error("Error fetching user versant summary:", error);
      throw error;
    }
  }

  async getUserCoursesSummary(user_id: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/user/${user_id}/courses-summary`);
    } catch (error) {
      logger.error("Error fetching user courses summary:", error);
      throw error;
    }
  }

  async getUserTrainingActivity(user_id: string): Promise<any> {
    try {
      return await apiClient.get(`/institution/user/${user_id}/training-activity`);
    } catch (error) {
      logger.error("Error fetching user training activity (institution):", error);
      return { success: 0, data: [] };
    }
  }

  async getLiveMetrics(): Promise<any> {
    try {
      return await apiClient.get("/institution/analytics/live-metrics");
    } catch (error) {
      logger.error("Error fetching live metrics:", error);
      throw error;
    }
  }

  async getLiveActivity(): Promise<any> {
    try {
      return await apiClient.get("/institution/analytics/live-activity");
    } catch (error) {
      logger.error("Error fetching live activity:", error);
      throw error;
    }
  }

  /**
   * Get institution assessment report leaderboard rankings
   * GET /institution/reports/assessment-leaderboard/:examId
   */
  async getAssessmentLeaderboard(examId: string, params?: Record<string, any>): Promise<any> {
    try {
      const query = params ? `?${new URLSearchParams(params).toString()}` : "";
      return await apiClient.get(`/institution/reports/assessment-leaderboard/${examId}${query}`);
    } catch (error) {
      logger.error("Error fetching institution assessment leaderboard:", error);
      throw error;
    }
  }

  /**
   * Get question banks assigned to the institution by the super admin.
   * Endpoint: GET /institution/question-bank-access
   */
  async getAssignedQuestionBanks(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    data: Array<{
      module_name: string;
      module_id: string;
      access_to: string;
      access_role: string;
      chapter_count: number;
      questions_count: number;
    }>;
  }> {
    try {
      const query_params = params
        ? "?" +
        Object.entries(params)
          .filter(([_, v]) => v !== undefined)
          .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
          .join("&")
        : "";
      return await apiClient.get(
        `/institution/question-bank-access${query_params === "?" ? "" : query_params}`,
      );
    } catch (error) {
      logger.error("Error fetching assigned question banks for institution:", error);
      throw error;
    }
  }

  /**
   * Get chapters for a specific assigned question bank module.
   * Endpoint: GET /institution/question-bank-access/:id/chapters
   */
  async getAssignedQuestionBankChapters(id: string): Promise<{
    success: boolean;
    data: Array<{ id: string; name: string }>;
  }> {
    try {
      return await apiClient.get(
        `/institution/question-bank-access/${encodeURIComponent(id)}/chapters`,
      );
    } catch (error) {
      logger.error("Error fetching assigned question bank chapters for institution:", error);
      throw error;
    }
  }

  /**
   * Get questions for a specific chapter in an assigned question bank module.
   * Endpoint: GET /institution/question-bank-access/:id/questions
   */
  async getAssignedQuestionBankQuestions(chapter_id: string): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      question: string;
      type: string;
      difficulty: string;
    }>;
  }> {
    try {
      return await apiClient.get(
        `/institution/question-bank-access/${encodeURIComponent(chapter_id)}/questions`,
      );
    } catch (error) {
      logger.error("Error fetching assigned question bank questions for institution:", error);
      throw error;
    }
  }

  /**
   * Get historical analytics for institution
   * POST /institution/analytics/historical
   */
  async getHistoricalAnalytics(params: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/historical", params || {});
    } catch (error) {
      logger.error("Error fetching institution historical analytics:", error);
      throw error;
    }
  }

  /**
   * Get batch performance report for institution
   * POST /institution/analytics/batch-performance
   */
  async getBatchPerformanceReport(params: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/batch-performance", params || {});
    } catch (error) {
      logger.error("Error fetching institution batch performance:", error);
      throw error;
    }
  }

  /**
   * Get Candidate 360 Master Report for institution
   * POST /institution/analytics/candidate-360-report
   */
  async getCandidate360Report(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/candidate-360-report", payload || {});
    } catch (error) {
      logger.error("Error fetching institution candidate 360 report:", error);
      throw error;
    }
  }

  /**
   * Get Student Coding Profiles Report for institution
   * POST /institution/reports/coding-profiles
   */
  async getCodingProfilesReport(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/reports/coding-profiles", payload || {});
    } catch (error) {
      logger.error("Error fetching institution coding profiles report:", error);
      throw error;
    }
  }

  // ─── Analytics Drilldown Services (Institution) ───────────────────────────

  async getHistoricalDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution historical drilldown:", error);
      throw error;
    }
  }

  async getAssessmentsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/assessments/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution assessments drilldown:", error);
      throw error;
    }
  }

  async getPracticeDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/practice/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution practice drilldown:", error);
      throw error;
    }
  }

  async getCoursesDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/courses/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution courses drilldown:", error);
      throw error;
    }
  }

  async getVersantDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/versant/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution versant drilldown:", error);
      throw error;
    }
  }

  async getExamAttemptsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/attempts/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution exam attempts drilldown:", error);
      throw error;
    }
  }

  async getQualifiedStudentsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/qualified/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution qualified students drilldown:", error);
      throw error;
    }
  }

  async getMcqQuestionsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/questions/mcq/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution mcq questions drilldown:", error);
      throw error;
    }
  }

  async getCodingQuestionsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/questions/coding/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution coding questions drilldown:", error);
      throw error;
    }
  }

  async getCodingSubmissionsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/submissions/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution coding submissions drilldown:", error);
      throw error;
    }
  }

  async getTrainingsDrilldown(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/analytics/trainings/drilldown", payload);
    } catch (error) {
      logger.error("Error fetching institution trainings drilldown:", error);
      throw error;
    }
  }

  async getStudentPerformanceReport(payload: Record<string, any>): Promise<any> {
    try {
      return await apiClient.post("/institution/reports/student-performance", payload);
    } catch (error) {
      logger.error("Error fetching student performance report:", error);
      throw error;
    }
  }
}

export const institutionService = new InstitutionService();
